import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  TextField,
  IconButton,
  List,
  ListItem,
  Paper,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Chip,
  Tooltip,
  Checkbox,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import SendIcon from "@mui/icons-material/Send";
import ChatbotSuggestions from "./ChatbotSuggestions";
import ChatbotTextResponse from "./ChatbotTextResponse";
import ChatbotCharts from "./ChatbotCharts";
import PersonIcon from "@mui/icons-material/Person";
import AvatarPng from "../assets/robot.png";
import { useChatbotContext } from "../context/ChatbotContext";
import axiosInstance from "../utils/axios";
import DownloadIcon from "@mui/icons-material/Download";
import { useClientContext } from "../context/ClientContext";
import LanguageSwitcher from "./LanguageSwitcher";
import LikeResponse from "./LikeResponse";
import MessageOutlinedIcon from "@mui/icons-material/MessageOutlined";
import Loader_GIF from "../assets/loader.gif";
import FeedbackMenu from "./FeedbackMenu";
import {
  suggestedQuestions,
  initialMessagesData,
} from "../assets/suggestedPrompts";
import SourceDialog from "./SourceDialog";
import { useGlobalModalContext } from "../context/GlobalModalContext";
import ChatbotInitialPrompt from "./ChatbotInitialPrompt";
import { useAuth } from "../context/AuthContextProvider";
import { useLanguageContext } from "../context/LanguageContext";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import UploadFileDialog from "./Modal/UploadFileDialog";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import DraftsIcon from "@mui/icons-material/Drafts";
import KeyboardVoiceIcon from "@mui/icons-material/KeyboardVoice";
import EmailMeetingMinuteDialog from "./Modal/EmailMeetingMinuteDialog";
import CustomTooltip from "./CustomTooltip";
import DOMPurify from "dompurify";
import { renderBoldText } from "../utils/helper";
import { useUploadContext } from "../context/UploadContext";
import ClearIcon from "@mui/icons-material/Clear";
import MessageDialog from "./Modal/MessageDialog";
import { useLoading } from "../context/LoadingContext";

function CustomChatbot({ height, selectedTab }) {
  const { userId } = useAuth();
  const { messages, setMessages, sessionId, setSessionId } = useChatbotContext();
  const [input, setInput] = useState("");
  const chatContainerRef = useRef(null); // Ref for the chat container
  const [switchState, setSwitchState] = useState(false);
  const textResponseRef = useRef(null); // Ref for text responses
  const chartResponseRef = useRef(null); // Ref for chart responses
  const [loading, setLoading] = useState(false);
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { clientName, clientGroup } = useClientContext();
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorElMenu1, setAnchorElMenu1] = useState(null);
  const [anchorElMenuFeedback, setAnchorElMenuFeedback] = useState(null);
  const [feedbackAnchorEls, setFeedbackAnchorEls] = useState({});
  const open = Boolean(anchorEl);
  const openMenu1 = Boolean(anchorElMenu1);
  const openFeedback = Boolean(anchorElMenuFeedback);
  const [suggestedQuestionOptions, setSuggestedQuestionOptions] = useState([]);
  const [initialMessages, setInitialMessages] = useState([]);
  const { showModal, hideModal } = useGlobalModalContext();
  const { username } = useAuth();
  const { language, setLanguage } = useLanguageContext();
  const [selectedQuestions, setSelectedQuestion] = useState(new Set());
  const selectedMessageContainer = useRef(null);
  const [expandedMessages, setExpandedMessages] = useState({});
  const [overflowStates, setOverflowStates] = useState({});
  const messageRefs = useRef([]);
  const { uploadData, setUploadData } = useUploadContext();
  const { showLoading, hideLoading } = useLoading();
  const iconButtonCss = {
    ml: 2,
    color: "#00555C",
    borderColor: "#00555C",
    "&:hover": {
      borderColor: "#294E59",
      backgroundColor: "#f0f4f8",
    },
  };
  const [chatHistory, setChatHistory] = useState([]);
  const [activeChat, setActiveChat] = useState(null);

  useEffect(() => {
    fetchChatHistory();
  }, []);

  useEffect(() => {
    const selection = suggestedQuestions[selectedTab];
    const initialMessageList = initialMessagesData(username)[selectedTab];
    setSuggestedQuestionOptions(selection);
    setInitialMessages(initialMessageList);
  }, [selectedTab]);


  // chat history logic starts here
  const fetchChatHistory = async () => {
    showLoading()
    try {
      const response = await axiosInstance.get(`/process_query/list_chats/${userId}`);
      //const response = await axiosInstance.get(`/list_chats`);
      setChatHistory(response.data?.sessions);
      hideLoading()
    } catch (error) {
      console.error("Error fetching chat history:", error);
      hideLoading()
    }
  };

  const createNewChat = async () => {
    try {
      const response = await axiosInstance.post("/new_chat");
      setSessionId(response.data?.session_id);
      setMessages([]);
      if (response.data) {
        fetchChatHistory();
      }
    } catch (error) {
      console.error("Error creating new chat:", error);
    }
  };

  const loadChatData = async (chat_id, chat) => {
    showLoading()
    try {
      const response = await axiosInstance.get(`/process_query/load_chat/${userId}/${chat_id}`);
      //const response = await axiosInstance.get(`/load_chat`);
      if (response.data) {
        fetchChatHistory();
        setActiveChat(chat);
        setSessionId(chat_id);
        //console.log('response', response.data);
        const processedMessages = response.data?.conversation?.map((msg) => {
          // Only include messages where both question and answer exist
          // console.log('msg', msg.user_query);
          // console.log('msg.response', msg.response);
          if (msg.user_query && msg.response) {
            // Create user message
            const userMessage = {
              type: "question",
              text: msg.user_query
            };
            
            const parsedResponse = () => {
              try {
                const parsed = JSON.parse(msg.response);
                return typeof parsed === "object" && parsed !== null ? parsed : msg.response;
              } catch (e) {
                return msg.response;
              }
            };

            const parsedResponseValue = parsedResponse();
            
            // Create bot response
            const botResponse = {
              question: msg.user_query,
              question_id: msg.question_id,
              session_id: chat_id,
              type: "answer", 
              response: {
                text: "",
                html: "",
                description: typeof parsedResponseValue === "object" ? parsedResponseValue?.description : [],
                strDescription: typeof parsedResponseValue === "string" ? parsedResponseValue : "",
                chart: msg.chart_save || [],
                suggestions: [],
                query_answered: msg.query_answered || 0,
              },
              //chartsData: msg.chart_save ? JSON.parse(msg.chart_save.replace(/'/g, '"')) : [],
              chartType: "bar",
              chartsData: msg.chart_save || [],
              code: msg.basic_code || "",
              timestamp: msg.timestamp || ""
            };

            //console.log(botResponse);

            return [userMessage, botResponse];
          }
          return [];
        }).flat();

        setMessages(processedMessages);
        hideLoading()
      }
    } catch (error) {
      console.error("Error loading chat history:", error);
      hideLoading()
    } 
  };

  // chat history logic ends here

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuClickMenu1 = (event) => {
    setAnchorElMenu1(event.currentTarget);
  };

  const handleMenuCloseMenu1 = () => {
    setAnchorElMenu1(null);
  };

  const handleMenuClickMenuFeedback = (event, index) => {
    setFeedbackAnchorEls((prev) => ({
      ...prev,
      [index]: event.currentTarget,
    }));
  };

  const handleMenuCloseMenuFeedback = (index) => {
    setFeedbackAnchorEls((prev) => ({
      ...prev,
      [index]: null,
    }));
  };

  const handleSwitchChange = (event) => {
    setSwitchState(event.target.checked);
  };

  const handleChipClick = (chip) => {
    setInput(chip); // set the input to the selected chip value
  };

  const toggleShowMore = (id) => {
    setExpandedMessages((prev) => ({
      ...prev,
      [id]: !prev[id], // Toggle the state for this specific message
    }));
  };

  useEffect(() => {
    const checkOverflow = () => {
      const newOverflowStates = {};
      messages.forEach((message, index) => {
        const element = messageRefs.current[index];
        if (element) {
          newOverflowStates[index] = element.scrollHeight > 300; // Check if content overflows
        }
      });
      setOverflowStates(newOverflowStates);
    };

    checkOverflow(); // Initial check
    window.addEventListener("resize", checkOverflow); // Recheck on window resize

    return () => {
      window.removeEventListener("resize", checkOverflow);
    };
  }, [messages]);

  //console.log("***selected Client", clientName);
  const handleSend = () => {
    if (input.trim() === "") return;
    const userMessage = { type: "question", text: input };
    // Append the chatbot's response
    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setLoading(true);
    setInput("");
    //console.log("***userTimezone", userTimezone);
    // API call
    if (selectedTab === "clientMetric") {
      handleSTRQuery();
    } else {
      handleUNSTRQuery();
    }
  };

  const handleUNSTRQuery = () => {
    axiosInstance
      .post("/process_query", {
        query: input,
        client_id: clientGroup,
        language: language,
        session_id: sessionId,
        user_id: userId,
        upload_flag: uploadData ? 2 : 1,
      })
      .then(function (response) {
        const answer = response.data;

        if (answer) {
          const botResponse = {
            // saving question with bot response so that its easier to reference later
            question: input,
            type: "answer",
            question_id: answer?.question_id,
            session_id: answer?.session_id,
            response: {
              //text: answer.response || "",
              description: answer.response?.description || [],
              //html: answer.response || "",
              chart: answer.chartData || [],
              suggestions: answer.response?.suggested_questions || [],
            },
            chartsData: answer.chartData || [],
            code: answer.code || "",
            timestamp: answer.timestamp || "",
            uploadFlag: uploadData ? 2 : 1,
          };

          setMessages((prevMessages) => [...prevMessages, botResponse]);
        }
      })
      .catch((error) => {
        console.error("Error while fetching chatbot response:", error);
      })
      .finally(() => setLoading(false));
  };

  const handleSTRQuery = () => {
    axiosInstance
      .post("/process_query/structured", {
        question: input,
        group_company: clientGroup,
        company_name: clientName,
        user_id: userId,
        language: language,
        session_id: sessionId,
        upload_flag: uploadData ? 2 : 1,
      })
      .then(function (response) {
        const answer = response.data;

        if (answer) {
          const botResponse = {
            // saving question with bot response so that its easier to reference later
            question: answer.question,
            question_id: answer.question_id,
            session_id: answer.session_id,
            type: "answer",
            response: {
              strDescription: answer.answer,
              //text: answer.answer,
              //html: answer.answer || "",
              chart: answer.chartData || [],
              suggestions: answer.suggested_questions || "",
              uploadFlag: uploadData ? 2 : 1,
            },
            chartType: "bar",
            chartsData: answer.chartData || [],
            code: answer.code || "",
            timestamp: answer.timestamp || "",
          };

          setMessages((prevMessages) => [...prevMessages, botResponse]);
        }
      })
      .catch((error) => {
        console.error("Error while fetching chatbot response:", error);
      })
      .finally(() => setLoading(false));
  };
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSend();
    }
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const handleDownloadHTML = () => {
    const chatbotContent =
      selectedQuestions.size > 0
        ? selectedMessageContainer.current?.innerHTML
        : chatContainerRef.current?.innerHTML; // Get the inner HTML of the chat container
    if (!chatbotContent) {
      alert("No content available to download.");
      return;
    }

    // Get all stylesheets and inline styles
    const styles = Array.from(document.styleSheets)
      .map((styleSheet) => {
        try {
          return Array.from(styleSheet.cssRules || [])
            .map((rule) => rule.cssText)
            .join("\n");
        } catch (error) {
          // Ignore cross-origin stylesheets
          console.warn(
            "Could not access styles from stylesheet",
            styleSheet.href
          );
          return "";
        }
      })
      .join("\n");

    const htmlTemplate = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Chatbot Conversation</title>
                <style>
                    ${styles}
                </style>
            </head>
            <body>
                <div>
                    ${chatbotContent}
                </div>
            </body>
            </html>
        `;

    const blob = new Blob([htmlTemplate], { type: "text/html" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "chatbot-conversation.html";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSourceClick = (metadata) => {
    showModal(<SourceDialog metadata={metadata} />);
  };

  const getSelectedMessages = () => {
    const filterMessages = messages.filter((message, index) =>
      selectedQuestions.has(index)
    );

    return filterMessages || [];
  };

  const handleCheckbox = (index) => {
    const questions = new Set(selectedQuestions);
    if (questions.has(index)) {
      questions.delete(index); // remove questions
      questions.delete(index + 1); // remove answer
    } else {
      questions.add(index); // add
      questions.add(index + 1); // add answer
    }
    setSelectedQuestion(questions);
  };

  const handleEmail = (data) => {
    showModal(<EmailMeetingMinuteDialog emails={""} />);
  };

  const handleRemoveUpload = () => {
    showModal(
      <MessageDialog
        message="Remove file?"
        primaryButtonText="YES"
        secondaryButtonText="NO"
        onPrimaryClick={() => {
          setUploadData(null);
        }}
        onSecondaryClick={() => {
          hideModal();
        }}
      />
    );
  };

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        p: 1,
        borderRadius: "8px",
        boxShadow: 2,
        height: height || "70vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Chat Header */}
      <div className="flex justify-between">
        <div className="flex justify-start items-center">
          <IconButton
            onClick={handleMenuClick}
            sx={{ color: "#376B7E" }}
            aria-controls={open ? "chatbot-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
          >
            <MenuIcon />
          </IconButton>
          <Menu
            id="chatbot-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            MenuListProps={{
              "aria-labelledby": "basic-button",
            }}
            PaperProps={{
              style: {
                maxHeight: 300,
                width: 250,
              },
            }}
          >
            <div className="text-sm font-semibold pl-4">
              <i>Chat History</i>
            </div>
            {chatHistory?.map((chat) => (
              <MenuItem 
                key={chat.session_id}
                onClick={() => {
                  loadChatData(chat.session_id, chat);
                  handleMenuClose();
                }}
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis"
                }}
              >
                <Tooltip title={chat.title}>
                  <span className="text-xs">
                    <i>{chat.title}</i>
                  </span>
                </Tooltip>
              </MenuItem>
            ))}
          </Menu>
          <span className="text-sm font-semibold" style={{ color: "#6B7280" }}>
            <i>Ask Coba</i>
          </span>
        </div>
        <div className="flex items-center justify-end">
          {/* <span className="text-sm font-semibold" style={{ color: "#376B7E" }}>
            Exclude Charts
          </span>
          <Switch
            checked={switchState}
            onChange={handleSwitchChange}
            color="primary"
            sx={{
              ml: 0,
              "& .MuiSwitch-thumb": {
                backgroundColor: switchState ? "#376B7E" : "#8FA9AE",
              },
              "& .MuiSwitch-track": {
                backgroundColor: switchState ? "#294E59" : "#B0C4C9",
              },
            }}
          /> */}
          <CustomTooltip title="Functionality not integrated with email client">
            <IconButton color="primary" onClick={() => handleEmail()}>
              <DraftsIcon />
            </IconButton>
          </CustomTooltip>
          <Tooltip title="Click to download completed chat history or select questions which you want to download and then click this button">
            <IconButton
              onClick={handleDownloadHTML}
              title=""
              sx={{ padding: 0 }}
              size="small"
            >
              <DownloadIcon color="primary" />
            </IconButton>
          </Tooltip>
          <LanguageSwitcher />
        </div>
      </div>

      {/* Chat Window */}
      <Paper
        ref={chatContainerRef} // Attach the ref here
        sx={{
          flex: 1,
          overflowY: "auto",
          mb: 2,
          //p: 2,
          backgroundColor: "#f9f9f9",
          borderRadius: "8px",
        }}
      >
        {messages && messages.length == 0 && (
          <>
            <div
              className="flex align-center justify-center h-[90%]"
              style={{ width: "100%", fontSize: "12px" }}
            >
              <div className="flex ml-1 mt-4">
                <Avatar
                  src={AvatarPng}
                  sx={{
                    width: 32,
                    height: 32,
                    p: "2px",
                    mb: 1, // Adds spacing below the avatar
                  }}
                />
                <ChatbotInitialPrompt initialMessage={initialMessages} />
              </div>
            </div>
            <div className="flex items-center">
              <IconButton
                onClick={handleMenuClickMenu1}
                sx={{ color: "#376B7E" }}
                aria-controls={open ? "chatbot-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
              >
                <MessageOutlinedIcon />
              </IconButton>
              <div className="text-[8px]">
                <i>Suggested Questions</i>
              </div>
            </div>
            <Menu
              id="chatbot-menu"
              anchorEl={anchorElMenu1}
              open={openMenu1}
              onClose={handleMenuCloseMenu1}
              MenuListProps={{
                "aria-labelledby": "basic-button",
              }}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
            >
              <div className="text-[8px] pl-4">
                <i>Suggested Prompts</i>
              </div>
              {suggestedQuestionOptions.map((item, index) => (
                <MenuItem key={index} onClick={handleMenuCloseMenu1}>
                  <Chip
                    label={item}
                    clickable
                    onClick={() => handleChipClick(item)}
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "normal",
                      fontSize: "12px",
                      maxHeight: "20px",
                    }}
                  />
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
        {messages && messages.length > 0 && (
          <>
            <List>
              {messages.map((message, index) => (
                <ListItem
                  key={index}
                  sx={{
                    justifyContent:
                      message.type === "question" ? "flex-end" : "flex-start",
                  }}
                >
                  {message.type === "question" ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end", // Aligns content to the right
                        alignItems: "center",
                        mb: 2, // Adds spacing between messages
                        cursor: "pointer",
                      }}
                      onClick={() => handleCheckbox(index)}
                    >
                      <Paper
                        sx={{
                          p: 1,
                          maxWidth: "80%",
                          textAlign: "right",
                          backgroundColor: "#f0f4f8",
                          borderRadius: "8px",
                          fontSize: "12px",
                        }}
                      >
                        {message.text}
                      </Paper>
                      <Avatar
                        alt={"user name"} // User's name or identifier
                        sx={{
                          width: 32,
                          height: 32,
                          ml: 1, // Adds space between the message and avatar
                        }}
                      >
                        <PersonIcon />
                      </Avatar>
                      {selectedQuestions.size > 0 && (
                        <Checkbox
                          size=""
                          sx={{ fontSize: "16px" }}
                          checked={selectedQuestions.has(index)}
                          onClick={() => handleCheckbox(index)}
                        />
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ maxWidth: "80%" }}>
                      <div ref={textResponseRef}>
                        {/* Feedback Before */}
                        <div className="ml-10 text-[10px] mb-1">
                          <i>
                            This is an AI generated response, please review
                            before using
                          </i>
                        </div>
                        {/* Structured Response */}
                        {message.response.strDescription &&
                          message.response.strDescription.length > 0 && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                mb: 1,
                              }}
                            >
                              <Avatar
                                src={AvatarPng}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  p: "2px",
                                  mr: 1,
                                }}
                              />
                              <Paper
                                sx={{
                                  p: 1,
                                  maxWidth: "80%",
                                  backgroundColor: "#f0f4f8",
                                  borderRadius: "8px",
                                  fontSize: "12px",
                                  whiteSpace: "pre-wrap",
                                }}
                              >
                                <div>
                                  {/* <div style={{ maxHeight: message.showMore ? 'none' : '100px', overflow: 'hidden' }}>
                                    {renderBoldText(message.response.strDescription)}
                                  </div> */}
                                  {/* <div
                                    key={index}
                                    style={{ marginBottom: "10px" }}
                                  >
                                    <div
                                      style={{
                                        maxHeight: expandedMessages[index]
                                          ? "none"
                                          : "300px",
                                        overflow: "hidden",
                                      }}
                                    >
                                      {renderBoldText(
                                        message.response.strDescription
                                      )}
                                    </div>
                                    <button
                                      style={{
                                        float: "right",
                                        color: "#00555C", 
                                        cursor: "pointer",
                                        fontSize: "12px",
                                        marginTop: "4px",
                                      }}
                                      onClick={() => toggleShowMore(index)}
                                    >
                                      {expandedMessages[index]
                                        ? "Show Less"
                                        : "Show More"}
                                    </button>
                                  </div> */}

                                  <div
                                    key={index}
                                    style={{ marginBottom: "10px" }}
                                  >
                                    <div
                                      ref={(el) =>
                                        (messageRefs.current[index] = el)
                                      }
                                      style={{
                                        maxHeight: expandedMessages[index]
                                          ? "none"
                                          : "300px",
                                        overflow: "hidden",
                                      }}
                                    >
                                      {renderBoldText(
                                        message.response.strDescription
                                      )}
                                    </div>
                                    {overflowStates[index] && ( // Show button only if content overflows
                                      <button
                                        style={{
                                          float: "right",
                                          color: "#00555C",
                                          cursor: "pointer",
                                          fontSize: "12px",
                                          marginTop: "4px",
                                        }}
                                        onClick={() => toggleShowMore(index)}
                                      >
                                        {expandedMessages[index]
                                          ? "Show Less"
                                          : "Show More"}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </Paper>
                            </Box>
                          )}
                        {/* Plain Text Response */}
                        {message.response.description &&
                          message.response.description.length > 0 && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                mb: 1,
                              }}
                            >
                              <Avatar
                                src={AvatarPng}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  p: "2px",
                                  mr: 1,
                                }}
                              />
                              <Paper
                                sx={{
                                  p: 1,
                                  maxWidth: "80%",
                                  backgroundColor: "#f0f4f8",
                                  borderRadius: "8px",
                                  fontSize: "12px",
                                  whiteSpace: "pre-wrap",
                                }}
                              >
                                {message.response.description.map(
                                  (desc, index) => (
                                    <div key={index}>
                                      {desc.title && (
                                        <div style={{ fontWeight: "bold" }}>
                                          {desc.title}
                                        </div>
                                      )}
                                      <div>{desc.text}</div>
                                      {message.response.uploadFlag == 2 && (
                                        <div>
                                          <i>
                                            Responses generated from the
                                            uploaded file.
                                          </i>
                                        </div>
                                      )}
                                      {desc.metadata &&
                                        desc.metadata.length > 0 && (
                                          <div
                                            className="text-right"
                                            style={{
                                              color: "#666",
                                              fontSize: "0.5rem",
                                              marginTop: "4px",
                                              cursor: "pointer",
                                              textDecoration: "underline",
                                            }}
                                            onClick={() =>
                                              handleSourceClick(desc.metadata)
                                            }
                                          >
                                            Source
                                          </div>
                                        )}
                                    </div>
                                  )
                                )}
                              </Paper>
                            </Box>
                          )}
                        {/* HTML Response */}
                        {message.response.html && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end", // Aligns content to the right
                              alignItems: "flex-start",
                              mb: 2, // Adds spacing between messages
                              fontSize: "10px",
                            }}
                          >
                            <Avatar
                              alt={"user name"} // User's name or identifier
                              src={AvatarPng}
                              sx={{
                                width: 32,
                                height: 32,
                                p: "2px",
                                mr: 1, // Adds space between the message and avatar
                              }}
                            />
                            <ChatbotTextResponse
                              html={message.response.html}
                              sqlCode={message.code}
                              chartsData={message.chartsData}
                              timestamp={message.timestamp}
                            />
                          </Box>
                        )}
                      </div>
                      {/* Chart Response */}
                      {message.response.chart &&
                        message.response.chart.length > 0 && (
                          <div ref={chartResponseRef}>
                            <Box sx={{ ml: 5 }}>
                              <ChatbotCharts data={message.response.chart} />
                            </Box>
                          </div>
                        )}
                      {/* Like Response */}
                      {message.response && (
                        <Box sx={{ ml: 5, p: 0 }}>
                          <LikeResponse message={message} />
                        </Box>
                      )}
                      {/* Feedback */}
                      <div className="flex items-center ml-10 text-[10px] mb-1">
                        <i>Please provide feedback</i>{" "}
                        <IconButton
                          sx={{
                            color: "#00555C",
                            fontSize: "14px",
                          }}
                          onClick={(e) => handleMenuClickMenuFeedback(e, index)}
                        >
                          <BorderColorIcon
                            className="text-[#00555C]"
                            fontSize="inherit"
                          />
                        </IconButton>
                      </div>
                      <Menu
                        id={`feedback-menu-${index}`}
                        anchorEl={feedbackAnchorEls[index]}
                        open={Boolean(feedbackAnchorEls[index])}
                        onClose={() => handleMenuCloseMenuFeedback(index)}
                        MenuListProps={{
                          "aria-labelledby": "basic-button",
                        }}
                        anchorOrigin={{
                          vertical: "top",
                          horizontal: "left",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "right",
                        }}
                      >
                        <FeedbackMenu
                          onClose={() => handleMenuCloseMenuFeedback(index)}
                          message={message}
                        />
                      </Menu>
                      {/* Suggestions Response */}
                      {message.response.suggestions && (
                        <Box sx={{ ml: 5, fontSize: "12px" }}>
                          <ChatbotSuggestions
                            suggestions={message.response.suggestions}
                            setInput={setInput}
                          />
                        </Box>
                      )}
                    </Box>
                  )}
                </ListItem>
              ))}
            </List>
            {/* Loading State */}
            {loading && (
              <div className="flex items-center">
                <Avatar
                  src={AvatarPng}
                  sx={{
                    width: 32,
                    height: 32,
                    p: "2px",
                    ml: 2,
                    mb: 1, // Adds spacing below the avatar
                  }}
                />
                <Paper
                  sx={{
                    mb: 1,
                    ml: 1,
                    width: "20%",
                    borderRadius: "8px",
                  }}
                >
                  <div className="flex h-[30px] justify-center">
                    <img src={Loader_GIF} />
                  </div>
                </Paper>
              </div>
            )}
          </>
        )}
      </Paper>

      {getSelectedMessages() && getSelectedMessages().length > 0 && (
        <Paper
          ref={selectedMessageContainer} // Attach the ref here
          sx={{
            flex: 1,
            overflowY: "auto",
            mb: 2,
            //p: 2,
            backgroundColor: "#f9f9f9",
            borderRadius: "8px",
            display: "none",
          }}
        >
          <>
            <List>
              {getSelectedMessages().map((message, index) => (
                <ListItem
                  key={index}
                  sx={{
                    justifyContent:
                      message.type === "question" ? "flex-end" : "flex-start",
                  }}
                >
                  {message.type === "question" ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end", // Aligns content to the right
                        alignItems: "center",
                        mb: 2, // Adds spacing between messages
                      }}
                    >
                      <Paper
                        sx={{
                          p: 1,
                          maxWidth: "80%",
                          textAlign: "right",
                          backgroundColor: "#f0f4f8",
                          borderRadius: "8px",
                          fontSize: "12px",
                        }}
                      >
                        {message.text}
                      </Paper>
                      <Avatar
                        alt={"user name"} // User's name or identifier
                        sx={{
                          width: 32,
                          height: 32,
                          ml: 1, // Adds space between the message and avatar
                        }}
                      >
                        <PersonIcon />
                      </Avatar>
                    </Box>
                  ) : (
                    <Box sx={{ maxWidth: "80%" }}>
                      <div ref={textResponseRef}>
                        {/* Feedback Before */}
                        {/* Plain Text Response */}
                        {message.response.description &&
                          message.response.description.length > 0 && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                mb: 1,
                              }}
                            >
                              <Avatar
                                src={AvatarPng}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  p: "2px",
                                  mr: 1,
                                }}
                              />
                              <Paper
                                sx={{
                                  p: 1,
                                  maxWidth: "80%",
                                  backgroundColor: "#f0f4f8",
                                  borderRadius: "8px",
                                  fontSize: "12px",
                                  whiteSpace: "pre-wrap",
                                }}
                              >
                                {message.response.description.map(
                                  (desc, index) => (
                                    <div key={index}>
                                      {desc.title && (
                                        <div style={{ fontWeight: "bold" }}>
                                          {desc.title}
                                        </div>
                                      )}
                                      <div>{desc.text}</div>
                                      {desc.metadata &&
                                        desc.metadata.length > 0 && (
                                          <div
                                            className="text-right"
                                            style={{
                                              color: "#666",
                                              fontSize: "0.5rem",
                                              marginTop: "4px",
                                              cursor: "pointer",
                                              textDecoration: "underline",
                                            }}
                                            onClick={() =>
                                              handleSourceClick(desc.metadata)
                                            }
                                          >
                                            Source
                                          </div>
                                        )}
                                    </div>
                                  )
                                )}
                              </Paper>
                            </Box>
                          )}
                        {/* HTML Response */}
                        {message.response.html && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end", // Aligns content to the right
                              alignItems: "flex-start",
                              mb: 2, // Adds spacing between messages
                              fontSize: "10px",
                            }}
                          >
                            <Avatar
                              alt={"user name"} // User's name or identifier
                              src={AvatarPng}
                              sx={{
                                width: 32,
                                height: 32,
                                p: "2px",
                                mr: 1, // Adds space between the message and avatar
                              }}
                            />
                            <ChatbotTextResponse
                              html={message.response.html}
                              sqlCode={message.code}
                              chartsData={message.chartsData}
                              timestamp={message.timestamp}
                            />
                          </Box>
                        )}
                      </div>

                      {/* Like Response */}
                      {message.response && (
                        <Box sx={{ ml: 5, p: 0 }}>
                          <LikeResponse />
                        </Box>
                      )}
                      {/* Feedback */}
                      <div className="flex items-center ml-10 text-[10px] mb-1">
                        <i>Please provide feedback</i>{" "}
                        <IconButton
                          sx={{
                            color: "#00555C",
                            fontSize: "14px",
                          }}
                          onClick={(e) => handleMenuClickMenuFeedback(e, index)}
                        >
                          <BorderColorIcon
                            className="text-[#00555C]"
                            fontSize="inherit"
                          />
                        </IconButton>
                      </div>
                      <Menu
                        id={`feedback-menu-${index}`}
                        anchorEl={feedbackAnchorEls[index]}
                        open={Boolean(feedbackAnchorEls[index])}
                        onClose={() => handleMenuCloseMenuFeedback(index)}
                        MenuListProps={{
                          "aria-labelledby": "basic-button",
                        }}
                        anchorOrigin={{
                          vertical: "top",
                          horizontal: "left",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "right",
                        }}
                      >
                        <FeedbackMenu
                          onClose={() => handleMenuCloseMenuFeedback(index)}
                          message={message}
                        />
                      </Menu>
                      {/* Suggestions Response */}
                      {message.response.suggestions && (
                        <Box sx={{ ml: 5, fontSize: "12px" }}>
                          <ChatbotSuggestions
                            suggestions={message.response.suggestions}
                            setInput={setInput}
                          />
                        </Box>
                      )}
                    </Box>
                  )}
                </ListItem>
              ))}
            </List>
          </>
        </Paper>
      )}

      {/* Input Box */}
      <Box sx={{ display: "flex", alignItems: "center", fontSize: "10px" }}>
        {!uploadData && (
          <Tooltip title="Click to upload document">
            <IconButton
              onClick={() => showModal(<UploadFileDialog />)}
              title=""
              sx={{ padding: 0.5 }}
              size="small"
            >
              <FileUploadIcon color="primary" />
            </IconButton>
          </Tooltip>
        )}

        {uploadData && (
          <Tooltip title="Click to remove document.">
            <IconButton
              onClick={handleRemoveUpload}
              title=""
              sx={{ padding: 0.5 }}
              size="small"
            >
              <ClearIcon color="primary" />
            </IconButton>
          </Tooltip>
        )}

        <CustomTooltip title="Functionality not yet integrated">
          <IconButton
            onClick={() => {}}
            title=""
            sx={{ padding: 0.5 }}
            size="small"
          >
            <KeyboardVoiceIcon color="primary" fontSize="small" />
          </IconButton>
        </CustomTooltip>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Ask a question..."
          size="small"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          sx={{
            borderRadius: "25px",
            backgroundColor: "#fff",
            ".MuiOutlinedInput-root": {
              borderRadius: "25px",
              fontSize: "12px",
              "& input": {
                fontSize: "12px",
              },
            },
          }}
        />
        <IconButton
          color="primary"
          onClick={handleSend}
          sx={{ ml: 1 }}
          disabled={input.trim().length < 1}
        >
          <SendIcon />
        </IconButton>
      </Box>
    </Box>
  );
}

export default CustomChatbot;
